class DiscountInput extends HTMLElement {
  updateCartDiscount = async (discount) => {
    const response = await fetch(
      `${window.Shopify.routes.root}cart/update.js`,
      {
        body: JSON.stringify({ discount }),
        method: "POST",
        headers: { "Content-Type": "application/json" }
      }
    );

    if (!response.ok) throw new Error("Unable to apply discount code");

    return response.json();
  };

  onApplyClick = async () => {
    const discountCode = this.input.value;

    if (!discountCode) {
      this.handleErrorMessage(this.missingDiscountCodeMessage);
      this.trackGaEvent(discountCode, false);

      return;
    }

    if (this.validDiscountCode.toLowerCase() === discountCode.toLowerCase()) {
      this.handleErrorMessage(this.enterDifferentDiscountCodeMessage);
      this.trackGaEvent(discountCode, false);

      return;
    }

    this.handleLoading();
    this.clearErrorMessage();

    try {
      const cart = await this.updateCartDiscount(discountCode);

      const discountApplicable =
        cart.discount_codes.find(({ code }) => code === discountCode)
          ?.applicable || false;

      if (discountApplicable) {
        this.clearBtn.style.setProperty("display", "block");
        this.validDiscountCode = discountCode;

        this.dispatch("success");
        this.trackGaEvent(discountCode);
      } else {
        this.handleErrorMessage(this.invalidDiscountCodeMessage);
        this.trackGaEvent(discountCode, false);
        this.updateCartDiscount(
          this.validDiscountCode ? this.validDiscountCode : ""
        );
      }
    } catch (error) {
      this.handleErrorMessage(this.genericErrorMessage);
      this.trackGaEvent(discountCode, false);
      console.error(error);
    } finally {
      this.unhandleLoading();
    }
  };

  onClearClick = async (event) => {
    event.preventDefault();

    this.handleLoading();
    this.clearErrorMessage();

    try {
      await this.updateCartDiscount("");

      this.input.value = "";
      this.validDiscountCode = "";
      this.clearBtn.style.setProperty("display", "none");

      this.dispatch("clear:success");
    } catch (error) {
      this.handleErrorMessage(this.genericErrorMessage);
      console.error(error);
    } finally {
      this.unhandleLoading();
    }
  };

  onInputKeydown = (event) => {
    if (event.key !== "Enter") return;

    event.preventDefault();
    this.onApplyClick();
  };

  handleLoading = () => {
    this.dispatch("loading:start");
    this.fieldset.disabled = true;
  };

  handleErrorMessage = (message) => {
    this.input.setCustomValidity(message);
    this.invalidFeedback.innerHTML = message;

    if (this.inputErrorClass && message) {
      this.input.classList.add(this.inputErrorClass);
    } else {
      this.input.classList.remove(this.inputErrorClass);
    }
  };

  clearErrorMessage = () => this.handleErrorMessage("");

  unhandleLoading = () => {
    this.dispatch("loading:end");
    this.fieldset.disabled = false;
  };

  trackGaEvent = (discountCode = "", submittedSuccesfully = true) => {
    if (
      this.dataset.trackGaEvent === "off" ||
      typeof window.gtag !== "function"
    )
      return;

    window.gtag("event", "cart_coupon", {
      coupon: discountCode,
      submitted_succesfully: submittedSuccesfully
    });
  };

  constructor() {
    super();

    this.input = this.querySelector("input");
    this.fieldset = this.querySelector("fieldset");
    this.applyBtn = this.querySelector('button[type="button"]');
    this.clearBtn = this.querySelector('button[type="reset"]');
    this.invalidFeedback = this.querySelector(".invalid-feedback");

    const {
      inputErrorClass,
      missingDiscountCodeMessage = "Please enter a discount code",
      enterDifferentDiscountCodeMessage = "Discount code already applied",
      invalidDiscountCodeMessage = "Invalid discount code",
      genericErrorMessage = "Something went wrong. Try again later"
    } = this.dataset;

    this.inputErrorClass = inputErrorClass;
    this.validDiscountCode = this.input.value || "";

    this.missingDiscountCodeMessage = missingDiscountCodeMessage;
    this.enterDifferentDiscountCodeMessage = enterDifferentDiscountCodeMessage;
    this.invalidDiscountCodeMessage = invalidDiscountCodeMessage;
    this.genericErrorMessage = genericErrorMessage;

    this.applyBtn.addEventListener("click", this.onApplyClick);
    this.clearBtn.addEventListener("click", this.onClearClick);
    this.input.addEventListener("keydown", this.onInputKeydown);
  }

  dispatch(type, detail) {
    this.dispatchEvent(new CustomEvent(type, { bubbles: false, detail }));
  }
}

if (!window.customElements.get("discount-input")) {
  window.customElements.define("discount-input", DiscountInput);
}
